import type { Distance } from "@pdfking/math"
import { Abs, Subtract, Total } from "@pdfking/math"
import { NullQ } from "@pdfking/structs"
import { GrowContentKind } from "@pdfking/templates"

import { EDGE } from "#constants"

import type { Box, Composition } from "./Composition"

/** One poster across a row of pages. */
export type PosterRow = {
  index: number
  /** The row's reach on the page, from its first page to its last */
  span: Box
  /** Its panels along the opening: the pages, and the gaps between them */
  widths: Distance[]
  /** Each page piece's panel */
  panel: Map<number, number>
  word_panel: number
  /** Whether the row carries page 1, and SAMPLE with it */
  sample: boolean
}

/** The page pieces in rows along the opening — those sharing their extent
 * across it —, by piece: one poster each, its panels the pieces in order,
 * from the left or from the top, and the gaps between them, the word on the
 * page numbered 1 or else the first. A turned row is drawn through a half
 * turn: the poster's first panel lands at the row's far end, so its panels
 * run the other way round. */
export const PosterRows = ({
  number,
  opening_top,
  pieces,
  placed: boxes,
  size: { height },
}: Composition): Map<number, PosterRow> => {
  // Where a box starts along the opening and how far it runs: from the left,
  // or from the top down
  const along = (box: Box): [Distance, Distance] =>
    opening_top ? [Subtract(height, Total(box.y, box.height)), box.height] : [box.x, box.width]
  const across = (box: Box): [Distance, Distance] =>
    opening_top ? [box.x, box.width] : [box.y, box.height]
  const near = (a: Distance, b: Distance) => Abs(Subtract(a, b)) <= EDGE
  const pages = pieces.flatMap((piece, i) => (piece.enum === GrowContentKind.PAGE ? [i] : []))
  const groups: number[][] = []
  for (const i of pages) {
    const [start, extent] = across(boxes[i]!)
    const group = groups.find(group => {
      const [s, e] = across(boxes[group[0]!]!)
      return near(s, start) && near(e, extent)
    })
    if (group) group.push(i)
    else groups.push([i])
  }
  const rows = new Map<number, PosterRow>()
  groups.forEach((group, index) => {
    group.sort((a, b) => along(boxes[a]!)[0] - along(boxes[b]!)[0])
    const widths: Distance[] = []
    const panel = new Map<number, number>()
    const [from] = along(boxes[group[0]!]!)
    let at = from
    for (const i of group) {
      const [start, extent] = along(boxes[i]!)
      if (Subtract(start, at) > EDGE) widths.push(Subtract(start, at))
      panel.set(i, widths.length)
      widths.push(extent)
      at = Total(start, extent)
    }
    if (pieces[group[0]!]!.rotation === 180) {
      widths.reverse()
      for (const [i, page] of panel) panel.set(i, widths.length - 1 - page)
    }
    const [across_start, across_extent] = across(boxes[group[0]!]!)
    const reach = Subtract(at, from)
    const span: Box = opening_top
      ? { height: reach, width: across_extent, x: across_start, y: Subtract(height, at) }
      : { height: across_extent, width: reach, x: from, y: across_start }
    const one = group.find(i => number(pieces[i]!) === 1)
    const row: PosterRow = {
      index,
      panel,
      sample: !NullQ(one),
      span,
      widths,
      word_panel: panel.get(one ?? group[0]!)!,
    }
    for (const i of group) rows.set(i, row)
  })
  return rows
}
